import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import { useMeetingsAccess } from '../utils/meetingsAccess';
import '../styles/meetings.css';

/**
 * The shared meeting link (/meet/abc-defg-hij), for somebody signed in to the
 * portal. If they are on the invite list (or it is a company-wide meeting) they
 * go straight to the room; anyone else asks to come in and waits here until a
 * host lets them in. Outside guests, who have no account, use pages/GuestMeeting.js.
 */

const POLL_MS = 2500;

const errorText = (err, fallback) => err.response?.data?.message || fallback;

const MeetingJoinLink = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const { enabled, loaded } = useMeetingsAccess();

    const [info, setInfo] = useState(null);
    const [problem, setProblem] = useState('');
    const [waiting, setWaiting] = useState(false);
    const [denied, setDenied] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;

        const poll = (waitToken, meetingId) => {
            api.get(`/meetings/link/${code}/knock/${waitToken}`)
                .then((res) => {
                    if (!alive) return;
                    if (res.data.status === 'admitted') { navigate(`/meetings/${meetingId}/room`, { replace: true }); return; }
                    if (res.data.status === 'denied') { setWaiting(false); setDenied(true); return; }
                    timer.current = setTimeout(() => poll(waitToken, meetingId), POLL_MS);
                })
                .catch((err) => {
                    if (!alive) return;
                    // A blip is not an answer: keep asking, unless the meeting is gone.
                    if (err.response?.status === 404) { setWaiting(false); setProblem('This meeting is no longer open.'); return; }
                    timer.current = setTimeout(() => poll(waitToken, meetingId), POLL_MS * 2);
                });
        };

        api.get(`/meetings/link/${code}`)
            .then(async (res) => {
                if (!alive) return;
                const found = res.data;
                setInfo(found);
                if (found.entry === 'direct') { navigate(`/meetings/${found._id}/room`, { replace: true }); return; }
                if (found.entry === 'blocked') { setProblem('The host removed you from this meeting.'); return; }
                if (found.locked) { setProblem('The host has locked this meeting.'); return; }

                // Not on the list: ask to come in, straight away - they followed the link to join.
                try {
                    const knock = await api.post(`/meetings/link/${code}/knock`);
                    if (!alive) return;
                    if (knock.data.status === 'admitted') { navigate(`/meetings/${knock.data.meetingId}/room`, { replace: true }); return; }
                    setWaiting(true);
                    poll(knock.data.waitToken, knock.data.meetingId);
                } catch (err) {
                    if (alive) setProblem(errorText(err, 'Could not ask to join.'));
                }
            })
            .catch((err) => { if (alive) setProblem(errorText(err, 'We could not find a meeting open for that link.')); });

        return () => { alive = false; clearTimeout(timer.current); };
    }, [enabled, code, navigate]);

    if (!loaded) return null;
    if (!enabled) return <Navigate to="/dashboard" replace />;

    return (
        <div className="mt-room-page">
            <div className="mt-center-card">
                {problem && (
                    <>
                        <h2>{problem}</h2>
                        <button type="button" className="mt-btn ghost" onClick={() => navigate('/meetings')}>Back to meetings</button>
                    </>
                )}

                {!problem && denied && (
                    <>
                        <h2>The host did not let you in.</h2>
                        <button type="button" className="mt-btn ghost" onClick={() => navigate('/meetings')}>Back to meetings</button>
                    </>
                )}

                {!problem && !denied && (
                    <>
                        <h2><FontAwesomeIcon icon={faVideo} /> {info?.title || 'Opening the meeting'}</h2>
                        {waiting ? (
                            <>
                                <p>
                                    <FontAwesomeIcon icon={faSpinner} spin /> Waiting for {info?.organizer || 'the host'} to let you in.
                                    This page opens the meeting as soon as they do.
                                </p>
                                <button type="button" className="mt-btn ghost" onClick={() => navigate('/meetings')}>Cancel</button>
                            </>
                        ) : (
                            <p><FontAwesomeIcon icon={faSpinner} spin /> One moment…</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default MeetingJoinLink;
